import type { components } from '@joyful/openapi/types.gen'
import type { EnumValueMeta } from './zod'
import { createEnumSchema } from './zod'
import type { UserCapability } from './capabilities'

export type UserRole = components['schemas']['UserRole']

export type AssignableUserRole = Exclude<UserRole, 'AGENT' | 'AUTOMATION'>

interface AssignableUserRoleMetadata extends EnumValueMeta {
  /**
   * @default false
   */
  canBePrimaryRcsm?: boolean
  /**
   * @default false
   */
  canImpersonateRoles?: boolean
  capabilities: {
    manage: UserCapability
    view: UserCapability
  }
  deprecationNotice?: string
}

interface UserRoleMetadata extends EnumValueMeta {
  /**
   * @default false
   */
  canBePrimaryRcsm?: boolean
  /**
   * @default false
   */
  canImpersonateRoles?: boolean
  capabilities?: AssignableUserRoleMetadata['capabilities']
  deprecationNotice?: string
}

const USER_ROLES_BEFORE_AGENT = [
  {
    capabilities: {
      manage: 'account.members.manage.practice_member',
      view: 'account.members.view.practice_member',
    },
    description:
      'View practice data and collaborate; limited reporting access.',
    title: 'Practice member',
    value: 'PRACTICE_MEMBER',
  },
  {
    capabilities: {
      manage: 'account.members.manage.practice_admin',
      view: 'account.members.view.practice_admin',
    },
    description:
      'Manage practice profile, members, documents, and practice settings.',
    title: 'Practice admin',
    value: 'PRACTICE_ADMIN',
  },
  {
    capabilities: {
      manage: 'account.members.manage.services_practice_admin',
      view: 'account.members.view.services_practice_admin',
    },
    deprecationNotice:
      'This role temporarily allows legacy services customers to have elevated access.',
    description:
      'Manage practice profile, members, documents, and practice settings.',
    title: 'Services practice admin',
    value: 'SERVICES_PRACTICE_ADMIN',
  },
  {
    capabilities: {
      manage: 'account.members.manage.biller',
      view: 'account.members.view.biller',
    },
    description:
      'Work assigned claims, update statuses, document actions, follow up.',
    title: 'Biller',
    value: 'BILLER',
  },
  {
    capabilities: {
      manage: 'account.members.manage.billing_supervisor',
      view: 'account.members.view.billing_supervisor',
    },
    description: 'Supervise queues, QA work, approve changes.',
    title: 'Billing supervisor',
    value: 'BILLING_SUPERVISOR',
  },
  {
    capabilities: {
      manage: 'account.members.manage.rcm_specialist',
      view: 'account.members.view.rcm_specialist',
    },
    description:
      'Work denials, rejections, and A/R with the agent. Handle claims the agent cannot finish.',
    title: 'RCM specialist',
    value: 'RCM_SPECIALIST',
  },
  {
    canBePrimaryRcsm: true,
    canImpersonateRoles: true,
    capabilities: {
      manage: 'account.members.manage.billing_manager',
      view: 'account.members.view.billing_manager',
    },
    description:
      'Manage billing operations, assignments, workflows, reporting, escalations.',
    title: 'RCSM',
    value: 'BILLING_MANAGER',
  },
] as const satisfies ReadonlyArray<{
  readonly canBePrimaryRcsm?: boolean
  readonly canImpersonateRoles?: boolean
  readonly capabilities: AssignableUserRoleMetadata['capabilities']
  readonly deprecationNotice?: string
  readonly description: string
  readonly title: string
  readonly value: AssignableUserRole
}>

const JOYFUL_ADMIN_USER_ROLE = {
  canBePrimaryRcsm: true,
  canImpersonateRoles: true,
  capabilities: {
    manage: 'account.members.manage.joyful_admin',
    view: 'account.members.view.joyful_admin',
  },
  description:
    'Full access: org settings, users, customers, integrations, billing.',
  title: 'Joyful Health admin',
  value: 'JOYFUL_ADMIN',
} as const satisfies {
  readonly canBePrimaryRcsm: true
  readonly canImpersonateRoles: true
  readonly capabilities: AssignableUserRoleMetadata['capabilities']
  readonly description: string
  readonly title: string
  readonly value: 'JOYFUL_ADMIN'
}

const AGENT_USER_ROLE = {
  description:
    'Service-only role used by the Joyful bot; managed through capability grants.',
  title: 'Agent',
  value: 'AGENT',
} as const satisfies {
  readonly description: string
  readonly title: string
  readonly value: 'AGENT'
}

const AUTOMATION_USER_ROLE = {
  description:
    'Service-only role used by deterministic automation actions; managed through capability grants.',
  title: 'Automation',
  value: 'AUTOMATION',
} as const satisfies {
  readonly description: string
  readonly title: string
  readonly value: 'AUTOMATION'
}

export const AssignableUserRoleSchema = createEnumSchema<
  AssignableUserRole,
  AssignableUserRoleMetadata
>([...USER_ROLES_BEFORE_AGENT, JOYFUL_ADMIN_USER_ROLE])

export const UserRoleSchema = createEnumSchema<UserRole, UserRoleMetadata>([
  ...USER_ROLES_BEFORE_AGENT,
  AGENT_USER_ROLE,
  AUTOMATION_USER_ROLE,
  JOYFUL_ADMIN_USER_ROLE,
])

export const UserRoleManagementCapabilities =
  AssignableUserRoleSchema.definitions.map((definition) => {
    return definition.capabilities.manage
  })

export function toAssignableUserRole(
  role: string | null | undefined,
  fallback: AssignableUserRole = 'PRACTICE_MEMBER',
): AssignableUserRole {
  const parsedRole = AssignableUserRoleSchema.schema.safeParse(role)
  if (parsedRole.success === false) {
    return fallback
  }

  return parsedRole.data
}

export function isAssignableUserRole(
  role: string | null | undefined,
): role is AssignableUserRole {
  return AssignableUserRoleSchema.safeMeta(role ?? '') !== undefined
}

export function filterAssignableUserRoles(
  roles: ReadonlyArray<string>,
): Array<AssignableUserRole> {
  return roles.flatMap((role) => {
    const parsedRole = AssignableUserRoleSchema.schema.safeParse(role)
    if (parsedRole.success === false) {
      return []
    }

    return [parsedRole.data]
  })
}

export function filterNonAssignableUserRoles(
  roles: ReadonlyArray<string>,
): Array<UserRole> {
  return roles.flatMap((role) => {
    if (isAssignableUserRole(role)) {
      return []
    }

    const parsedRole = UserRoleSchema.schema.safeParse(role)
    if (parsedRole.success === false) {
      return []
    }

    return [parsedRole.data]
  })
}

export function isBillingUserRole(role: string | null | undefined) {
  return (
    role === 'BILLER' ||
    role === 'BILLING_SUPERVISOR' ||
    role === 'RCM_SPECIALIST' ||
    role === 'BILLING_MANAGER'
  )
}

export function isPracticeUserRole(role: string | null | undefined) {
  return (
    role === 'PRACTICE_ADMIN' ||
    role === 'PRACTICE_MEMBER' ||
    role === 'SERVICES_PRACTICE_ADMIN'
  )
}
